import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material'
import {
  ArrowBack,
  Science,
  Search,
  LocationOn,
  FilterList,
  CheckCircle,
  OpenInNew,
  BookmarkAdd,
  Assessment,
} from '@mui/icons-material'

const TrialSearch: React.FC = () => {
  const navigate = useNavigate()

  const searchFeatures = [
    {
      icon: <Search fontSize="large" />,
      title: 'Smart Search',
      description: 'Search ClinicalTrials.gov database with intelligent matching',
      features: [
        'Search by condition or disease',
        'Filter by trial phase',
        'Status filtering (recruiting, active, completed)',
        'Keyword and condition matching',
      ]
    },
    {
      icon: <LocationOn fontSize="large" />,
      title: 'Location-Based',
      description: 'Find trials near you with geographic filtering',
      features: [
        'Search by ZIP code or city',
        'Customizable search radius',
        'Multiple location support',
        'Distance calculation',
      ]
    },
    {
      icon: <FilterList fontSize="large" />,
      title: 'Advanced Filters',
      description: 'Refine results with powerful filtering options',
      features: [
        'Trial phase selection',
        'Recruitment status',
        'Study type filtering',
        'Sponsor and condition filters',
      ]
    },
    {
      icon: <Assessment fontSize="large" />,
      title: 'Eligibility Analysis',
      description: 'Match your health profile to trial requirements',
      features: [
        'Automatic eligibility checking',
        'Match percentage scoring',
        'Inclusion/exclusion criteria analysis',
        'Save matched trials for review',
      ]
    },
  ]

  const benefits = [
    'Access to 400,000+ clinical trials',
    'Official ClinicalTrials.gov data',
    'Real-time trial information',
    'Save and track interesting trials',
    'Export trial information',
    'Direct links to official trial pages',
  ]

  const trialInfo = [
    'Trial title and description',
    'Eligibility criteria',
    'Study design and phases',
    'Location and contact information',
    'Recruitment status',
    'Study start and completion dates',
  ]

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        <Button 
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Home
        </Button>

        <Paper sx={{ p: 4 }}>
          <Box textAlign="center" mb={6}>
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Clinical Trial Search
            </Typography>
            <Typography variant="h5" color="text.secondary">
              Find clinical trials that match your health condition - powered by ClinicalTrials.gov
            </Typography>
          </Box>

          {/* Hero Section */}
          <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 4, borderRadius: 3, mb: 6 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Discover Research Opportunities
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Search through hundreds of thousands of clinical trials from the official ClinicalTrials.gov 
                  database. Our intelligent matching system helps you find trials that are relevant to your 
                  specific health condition.
                </Typography>
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                  onClick={() => navigate('/register')}
                >
                  Start Searching Trials
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    What You Get
                  </Typography>
                  <Stack spacing={2}>
                    {benefits.map((benefit, index) => (
                      <Box key={index} display="flex" alignItems="center" gap={1}>
                        <CheckCircle />
                        <Typography variant="body1">{benefit}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Search Features */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6, mb: 3 }}>
            Powerful Search Features
          </Typography>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {searchFeatures.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', '&:hover': { boxShadow: 6 } }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Box sx={{ color: 'primary.main' }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      {feature.description}
                    </Typography>
                    <List dense>
                      {feature.features.map((item, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircle color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Trial Information */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6, mb: 3 }}>
            Comprehensive Trial Information
          </Typography>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    What You'll See for Each Trial
                  </Typography>
                  <List>
                    {trialInfo.map((info, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <Science color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={info} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', bgcolor: 'primary.lighter' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Trial Management Tools
                  </Typography>
                  <Stack spacing={3} sx={{ mt: 2 }}>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <BookmarkAdd color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Save Trials
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Bookmark interesting trials for later review
                      </Typography>
                    </Box>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <OpenInNew color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Direct Links
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Access full trial details on ClinicalTrials.gov
                      </Typography>
                    </Box>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Assessment color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Track Eligibility
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Monitor your eligibility status for each trial
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* How It Works */}
          <Box sx={{ bgcolor: 'grey.50', p: 4, borderRadius: 3, mb: 6 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom textAlign="center">
              How Trial Matching Works
            </Typography>
            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" fontWeight="bold">1</Typography>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Build Profile
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enter your health conditions and medical history
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" fontWeight="bold">2</Typography>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Search Trials
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use filters to find relevant clinical studies
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" fontWeight="bold">3</Typography>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Review Matches
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check eligibility criteria and trial details
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" fontWeight="bold">4</Typography>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Contact Study
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reach out to trial coordinators directly
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Stats */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  400K+
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Clinical Trials
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  220+
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Countries
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  1000s
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Active Recruiting
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* CTA */}
          <Box textAlign="center" sx={{ mt: 6 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Ready to Find Your Clinical Trial Match?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Start searching hundreds of thousands of clinical trials today
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/register')}
              sx={{ px: 6, py: 2 }}
            >
              Get Started Free
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export default TrialSearch

